import { useCallback, useMemo, useState } from "react";

import { ContactBlacklistedProspect, ContactSelectType } from "../../Contacts/schema/contacts";
import { hasContactRecord } from "./blacklistRowId";

export interface BlacklistSelectionPayload {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
}

interface UseBlacklistSelectionParams {
	data: ContactBlacklistedProspect[] | undefined;
	filter: Record<string, unknown> | undefined;
	query: string;
	itemCount: number | undefined;
}

/**
 * Owns which Blacklist tab rows are selected and what a bulk action sends for them. Rows are a mix
 * of contacts and blacklist entries with no contact, so it also answers what the selection can
 * actually be acted on with — snoozing an entry row, for instance, has nothing to snooze.
 */
export const useBlacklistSelection = ({
	data,
	filter,
	query,
	itemCount,
}: UseBlacklistSelectionParams) => {
	const [selectedItems, setSelectedItems] = useState<number[]>([]);
	const [selectAll, setSelectAll] = useState(false);
	const [selectType, setSelectType] = useState<ContactSelectType>(ContactSelectType.CURRENT_PAGE);

	const selectedData = useMemo(
		() => (data || []).filter((row) => selectedItems.includes(row.id)),
		[data, selectedItems]
	);

	const selectedCount =
		selectType === ContactSelectType.ALL ? itemCount || 0 : selectedItems.length;

	const hasSnoozedItems = useMemo(() => selectedData.some((row) => row.snoozed), [selectedData]);

	/**
	 * Whether snooze/unsnooze apply at all. A "select all" spans contacts too, so it always can;
	 * an explicit selection only when it contains at least one contact row.
	 */
	const canSnooze =
		selectType === ContactSelectType.ALL || selectedData.some((row) => hasContactRecord(row));

	const getSelectionPayload = useCallback((): BlacklistSelectionPayload => {
		if (selectType === ContactSelectType.ALL) {
			return {
				selectType: ContactSelectType.ALL,
				...(filter && Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
				...(query && { searchValue: query }),
			};
		}

		return { selectType: ContactSelectType.CURRENT_PAGE, ids: selectedItems };
	}, [selectType, filter, query, selectedItems]);

	const clearSelection = useCallback(() => {
		setSelectedItems([]);
		setSelectAll(false);
		setSelectType(ContactSelectType.CURRENT_PAGE);
	}, []);

	const toggleSelectAll = useCallback(() => {
		if (selectAll) {
			clearSelection();

			return;
		}

		setSelectedItems((data || []).map((row) => row.id));
		setSelectAll(true);
	}, [selectAll, data, clearSelection]);

	const selectItem = useCallback((id: number) => {
		setSelectedItems((previous) =>
			previous.includes(id) ? previous.filter((item) => item !== id) : [...previous, id]
		);
		setSelectAll(false);
		setSelectType(ContactSelectType.CURRENT_PAGE);
	}, []);

	const applySelectType = useCallback(
		(type: ContactSelectType) => {
			setSelectedItems((data || []).map((row) => row.id));
			setSelectAll(true);
			setSelectType(type);
		},
		[data]
	);

	return {
		selectedItems,
		setSelectedItems,
		selectAll,
		selectType,
		selectedCount,
		hasSnoozedItems,
		canSnooze,
		getSelectionPayload,
		clearSelection,
		toggleSelectAll,
		selectItem,
		applySelectType,
	};
};
